/* eslint-disable react-hooks/exhaustive-deps */

import {
	Box,
	Paper,
	Skeleton,
	Table,
	TableBody,
	TableContainer,
	TableHead,
	Typography,
} from '@mui/material';
import { nanoid } from '@reduxjs/toolkit';
import { FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { PAGE_SIZE } from 'services/constants/env';
import {
	getErrorCorrectionsRequest,
	getErrorTypesRequest,
} from 'redux/reducers/errorCorrections/reducer';
import {
	getErrorCorrectionsLoader,
	getErrorCorrectionsList,
} from 'redux/reducers/errorCorrections/selectors';
import { useLocation } from 'react-router';
import queryString from 'query-string';
import { TFilter, TOrderBy } from 'redux/reducers/errorCorrections/types';
import { DATES_ARRAY } from 'services/constants/dates-constants';
import { COLORS } from 'services/constants/colors';
import { ErrorsRow } from './ErrorsRow';
import { PaginationBlock } from '../../../ui/PaginationBlock';
import { ErrorsHeader } from './ErrorsHeader';
import { FilterPanel } from './FilterPanel';
import { TTableStructure } from './types';
import { errorsTableStructure } from './errors-table-structure';
import { ActionPanel } from './ActionPanel';
import { UserStatistic } from './UserStatistic';
import { EasPagination } from '../../../ui/EasPagination';

const ROWS = Array.from(new Array(PAGE_SIZE / 2));

export const ErrorsTable: FC = () => {
	const tableStructure = errorsTableStructure() as TTableStructure[];

	const dispatch = useDispatch();
	useEffect(() => {
		dispatch(getErrorTypesRequest());
	}, [dispatch]);

	const { search } = useLocation();
	const location = useLocation();
	const navigateParams = queryString.parse(search);
	const { page } = queryString.parse(search);
	const navigateParamsWithoutPage = tableStructure.reduce((acc, item) => {
		if (navigateParams?.orderDate && item.selector === 'orderDate') {
			const dates = DATES_ARRAY.find((i) => i.id === navigateParams.orderDate);
			// console.log('dates', dates);
			if (dates?.from) {
				acc.push({
					fieldName: 'orderDate',
					value: dates.from,
					operator: '>=',
				});
			}
			if (dates?.to) {
				acc.push({
					fieldName: 'orderDate',
					value: dates.to,
					operator: '<=',
				});
			}
			return acc;
		}
		if (
			navigateParams[item.selector] &&
			item.selector === 'status' &&
			navigateParams.status === 'Not Fixed'
		) {
			acc.push({
				fieldName: 'status',
				value: 'Fixed',
				operator: '<>',
			});
		} else if (navigateParams[item.selector])
			acc.push({
				fieldName: item.selector,
				value: navigateParams[item.selector] as string,
				operator: '=',
			});
		return acc;
	}, [] as TFilter[]);

	const errors = useSelector(getErrorCorrectionsList);
	const errorsIsLoading = useSelector(getErrorCorrectionsLoader);
	const [selectedRow, setSelectedRow] = useState<number | null>(null);
	const [checkedRows, setCheckedRows] = useState<boolean[]>(errors?.data.map(() => false) ?? []);
	const [checkedAll, setCheckedAll] = useState(false);
	const [checkedIds, setCheckedIds] = useState<number[]>([]);

	useEffect(() => {
		setCheckedRows(errors?.data.map(() => false) ?? []);
		setCheckedAll(false);
		setCheckedIds([]);
	}, [errors]);

	const handleCheckAll = (checked: boolean) => {
		setCheckedAll(checked);
		setCheckedRows(errors?.data.map(() => checked) ?? []);
		if (checked) setCheckedIds(errors?.data.map((item) => item.id) ?? []);
		else setCheckedIds([]);
	};

	const handleCheckRow = (index: number, checked: boolean) => {
		const newCheckedRows = [...checkedRows];
		newCheckedRows[index] = checked;
		setCheckedRows(newCheckedRows);
		if (errors?.data[index].id) {
			if (checked) setCheckedIds([...(checkedIds ?? []), errors?.data[index].id]);
			else setCheckedIds(checkedIds.filter((id) => id !== errors?.data[index].id));
		}
	};

	useEffect(() => {
		const orderBy = [];
		if (navigateParams?.sortIndex) {
			orderBy.push({
				fieldName: navigateParams?.sortIndex
					? tableStructure[+navigateParams.sortIndex].selector
					: 'id',
				direction: navigateParams?.sortDirection
					? String(navigateParams.sortDirection) === 'desc'
						? 'desc'
						: 'asc'
					: 'desc',
			} as TOrderBy);
		}
		orderBy.push({
			fieldName: 'id',
			direction: 'desc',
		} as TOrderBy);

		dispatch(
			getErrorCorrectionsRequest({
				pageNumber: page ? +page : 1,
				pageSize: PAGE_SIZE,
				filters: navigateParamsWithoutPage,
				searchString: String(navigateParams?.search ?? ''),
				orderBy,
			}),
		);
	}, [dispatch, location.search]);

	// const filtersArray: TFilter[] = tableStructure.reduce((acc, item) => {
	// 	if (navigateParams[item.selector])
	// 		acc.push({ name: item.selector, value: navigateParams[item.selector] as string });
	// 	return acc;
	// }, [] as { name: string; value: string }[]);

	return (
		<Box>
			<UserStatistic />
			<Paper
				sx={{
					margin: '10px 10px 0 10px',
					maxWidth: 'calc(100vw - 20px)',
					overflowX: 'auto',
				}}
			>
				<Box sx={{ width: 'auto', minWidth: '1200px' }}>
					<Box sx={{ borderRadius: '12px' }}>
						<Box
							sx={{
								display: 'flex',
								justifyContent: 'space-between',
								alignItems: 'center',
								backgroundColor: COLORS.BACKGROUND_SECONDARY,
								p: 0.5,
							}}
						>
							<Typography variant="body1" color="primary" fontWeight="bold">
								Errors Corrections
							</Typography>
						</Box>
						{checkedIds.length > 0 ? (
							<ActionPanel errorIds={checkedIds} checkedRows={checkedRows} />
						) : (
							<FilterPanel tableStructure={tableStructure} />
						)}
						<TableContainer>
							<Table onMouseLeave={() => setSelectedRow(null)} onBlur={() => setSelectedRow(null)}>
								<TableHead>
									<ErrorsHeader
										checkedAll={checkedAll}
										setCheckAll={handleCheckAll}
										indeterminate={
											checkedIds.length > 0 && checkedIds.length < (errors?.data.length ?? 0)
										}
									/>
								</TableHead>
								{!errorsIsLoading && !!errors?.data.length && (
									<TableBody
										onMouseLeave={() => setSelectedRow(null)}
										onBlur={() => setSelectedRow(null)}
									>
										{!!errors?.data &&
											errors.data.map((item, index) => (
												<ErrorsRow
													key={item.id}
													item={item}
													setSelectedRow={setSelectedRow}
													selectedRow={selectedRow}
													index={index}
													checkedRow={checkedRows[index]}
													setCheckRow={(checked) => handleCheckRow(index, checked)}
												/>
											))}
									</TableBody>
								)}
							</Table>
							{errorsIsLoading && (
								<Box
									sx={{
										pb: '1px',
									}}
								>
									{ROWS.map(() => (
										<Skeleton
											key={nanoid()}
											variant="rectangular"
											height="33px"
											sx={{
												mb: '33px',
											}}
										/>
									))}
								</Box>
							)}
							{!errorsIsLoading && !errors?.data.length && (
								<Box
									sx={{
										display: 'flex',
										justifyContent: 'center',
										alignItems: 'center',
										height: '620px',
										width: '100%',
										color: COLORS.PRIMARY,
										fontSize: '24px',
										fontWeight: 'bold',
									}}
								>
									No error found...
								</Box>
							)}
						</TableContainer>
					</Box>
				</Box>
			</Paper>
			<EasPagination total={errors?.totalCount ?? 0} isLoading={errorsIsLoading} />
		</Box>
	);
};
